import { DatePipe, LowerCasePipe, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { ProjectSettingsService } from '@/app/partials/services/project-settings.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatNoDataRow,
  MatRow,
  MatTable,
  MatTableDataSource,
  MatTableModule
} from '@angular/material/table';
import { MatRipple } from '@angular/material/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { AmlScanEntity, AmlScanQueryParams, IngoingTransaction, LatestAmlReport, PaginationResponse, TableSetting, TableSettingRequest } from "../../../../../../api/api.model";
import { parseTransactionData } from '../../../../../../partials/helpers/transaction-parser.helper';
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { ApiService } from "../../../../../../api/api.service";
import { filter, Subscription, take } from "rxjs";
import { AuthService } from "../../../../../../partials/services/auth.service";
import { SidenavService } from '../../../../../../partials/services/sidenav.service';
import { ReportsComponent } from '../../../../../../partials/components/reports/reports.component';
import { TransformUnderscoreToSpacePipe } from '../../../../../../partials/pipes/tranform-underscore-to-space.pipe';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PopoverDirective } from '../../../../../../partials/module/popover/popover.directive';
import { PopoverContainerComponent } from '../../../../../../partials/module/popover/popover-container/popover-container.component';
import { ReportStatusComponent } from '../../../../../../partials/components/report-status/report-status.component';
import { TriggerIdConvertToCategoryPipe } from '../../../../../../partials/pipes/trigger-id-convert-to-category.pipe';
import { EmptyTableComponent } from '../../../../../../partials/components/empty-table/empty-table.component';
import { EmptyPaginationComponent } from '../../../../../../partials/components/empty-pagination/empty-pagination.component';
import { CutNumberPipe } from "../../../../../../partials/pipes/cut-number.pipe";
import { PrepareRiskCategoryToListPipe } from "@/app/partials/pipes/prepare-risk-category-to-list.pipe";
import { MonitoringFacade } from '@/app/pages/monitoring/monitoring.facade';
import { changeNameTrxToTronPipe } from "../../../../../../partials/pipes/change-name-trx-to-tron.pipe";
import { ActivatedRoute, Router } from '@angular/router';

// Настройки таблицы которые сохраняются в API
export interface MonitoringTableSettings {
  allDisplayedColumns: string[];
  filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const tableName = 'TransactionsTable';

@Component({
  selector: 'app-monitoring-table',
  imports: [
    MatIconModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatFormFieldModule,
    MatRipple,
    MatPaginatorModule,
    MatNoDataRow,
    DatePipe,
    MatMenuTrigger,
    MatMenu,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    MatCheckbox,
    FormsModule,
    MatTableModule,
    NgClass,
    TransformUnderscoreToSpacePipe,
    TitleCasePipe,
    MatSelectModule,
    ReactiveFormsModule,
    LowerCasePipe,
    PopoverDirective,
    PopoverContainerComponent,
    ReportStatusComponent,
    TriggerIdConvertToCategoryPipe,
    NgTemplateOutlet,
    EmptyTableComponent,
    EmptyPaginationComponent,
    CutNumberPipe,
    PrepareRiskCategoryToListPipe,
    changeNameTrxToTronPipe
],
  templateUrl: './monitoring-table.component.html',
  styleUrl: './monitoring-table.component.scss'
})
export class MonitoringTableComponent implements OnInit {
  private readonly projectSettingsService = inject(ProjectSettingsService);
  isAmlEnergyActive = computed(() => this.projectSettingsService.settings()?.isAmlEnergyActive ?? false);

  private readonly _apiService: ApiService = inject(ApiService);

  private _mainSidenavService: SidenavService = inject(SidenavService);

  private readonly _authService: AuthService = inject(AuthService);

  readonly pageEvent: OutputEmitterRef<PageEvent> = output<PageEvent>();

  readonly outputEventUpdateStatuses: OutputEmitterRef<void> = output<void>();

  readonly paginationResponseData: InputSignal<PaginationResponse<AmlScanEntity>> = input.required<PaginationResponse<AmlScanEntity>>();

  /** Параметры запроса после merge на фасаде — активные фильтры списка. */
  readonly transactionsParams: InputSignal<Partial<AmlScanQueryParams>> =
    input.required<Partial<AmlScanQueryParams>>();

  readonly isGrouped: InputSignal<boolean> = input.required<boolean>();

  readonly paginationOptions: InputSignal<{
    pageSize: number;
    pageIndex: number;
    pageSizeOptions: number[];
    length: number
  }> = input.required<{ pageSize: number; pageIndex: number; pageSizeOptions: number[]; length: number }>();

  public markOpenRow: WritableSignal<string> = signal('');
  public nicknamesList: WritableSignal<string[]> = signal([]);
  public sortingList: WritableSignal<{ amlScanTypes: string[], sourceTypes: string[] }> = signal({
    amlScanTypes: [],
    sourceTypes: []
  });

  private readonly _fb = inject(NonNullableFormBuilder);

  public filterForm: FormGroup = this._fb.group({
    nickname: this._fb.control<string | null>(null),
    amlScanType: this._fb.control<string | null>(null),
    sourceType: this._fb.control<string | null>(null),
  });

  readonly tableSettings: InputSignal<TableSetting<MonitoringTableSettings> | undefined> = input<TableSetting<MonitoringTableSettings> | undefined>();

  private readonly _monitoringFacade: MonitoringFacade = inject(MonitoringFacade);
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);
  private readonly _router: Router = inject(Router);
  allDisplayedColumns: string[] = [
    'date',
    'source',
    'risk_score',
    // 'deposit_wallet',
    'category',
    'nickname',
    'value',
    'coin',
    // 'monitoring_status',
    'network',
    'risky_amount',
    'report_type',
    'exposure',
    'service_fee',
    'blockchain_fee',
    'table_settings',
    'action',
  ];

  private _viewSubscription!: Subscription;

  filteredDisplayedColumns: string[] = [];

  get displayedColumns() {
    return this.allDisplayedColumns.filter((column) => !this.filteredDisplayedColumns.includes(column));
  };

  get displayedColumnsEmpty() {
    const displayedColumns = this.displayedColumns;
    return displayedColumns.filter(item => item !== 'table_settings');
  };

  readonly dataSource: MatTableDataSource<AmlScanEntity> = new MatTableDataSource<AmlScanEntity>([]);

  constructor() {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (value) => {
        this._monitoringFacade.loadTransactions({...value, page: 0});
      }
    });
    effect(() => {
      const { content } = this.paginationResponseData();
      this.dataSource.data = content;
    });

    effect(() => {
      const loadedTableSettings = this.tableSettings();

      if (loadedTableSettings) {
        const { data: { allDisplayedColumns, filteredDisplayedColumns } } = loadedTableSettings;
        // Проверка нужно для того чтоб если были изменения в allDisplayedColumns.
        // Мы не перезаписывали сохраненными настройками.
        if (
          this.allDisplayedColumns.every((displayedColumn) => allDisplayedColumns.includes(displayedColumn))
          &&
          allDisplayedColumns.every((displayedColumn) => this.allDisplayedColumns.includes(displayedColumn))
        ) {
          this.allDisplayedColumns = allDisplayedColumns;
          this.filteredDisplayedColumns = filteredDisplayedColumns;
        }
      }
    });
  }

  ngOnInit(): void {
    this._apiService.getWalletNicknames().pipe(take(1)).subscribe({
      next: (value: string[]) => {
        this.nicknamesList.set(value);
      }
    });
    this._apiService.getSortingOptionsMonitoring().pipe(take(1)).subscribe({
      next: (value: { amlScanTypes: string[], sourceTypes: string[] }) => {
        this.sortingList.set(value);
      }
    });

    const amlScanUuid = this._route.snapshot.queryParamMap.get('amlScanUuid');
    console.log('amlScanUuid', amlScanUuid);
    if (amlScanUuid) {
      this.viewReport(amlScanUuid);
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { amlScanUuid: null, process: null },
        queryParamsHandling: 'merge'
      });
    }
  }

  private _saveTableSettings(): void {
    const user = this._authService.accountDto();

    if (!user) return;

    const { uuid } = user;

    const body: TableSettingRequest = {
      data: {
        allDisplayedColumns: this.allDisplayedColumns,
        filteredDisplayedColumns: this.filteredDisplayedColumns,
      },
      tableName,
      accountId: uuid
    };
    this._apiService.createOrUpdateTableSetting<MonitoringTableSettings>(body).pipe(take(1)).subscribe({
      next: () => {
        console.log(`${ tableName } settings saved!`);
      }
    });
  }

  handlerChangeDisplayColumnCheckbox({ checked }: MatCheckboxChange, displayedColumn: string): void {
    if (checked) {
      this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter((c) => c !== displayedColumn);
    } else {
      this.filteredDisplayedColumns.push(displayedColumn);
    }
    this._saveTableSettings();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.allDisplayedColumns, event.previousIndex, event.currentIndex);
    this._saveTableSettings();
  }

  viewReport(uuid: string): void {
    this._viewSubscription?.unsubscribe();
    this._viewSubscription = this._apiService.getAmlScanByUuid(uuid).pipe(
      filter(Boolean)
    ).subscribe({
      next: (transactionEntity: AmlScanEntity) => {
        const transaction = parseTransactionData(transactionEntity);
        this.outputEventUpdateStatuses.emit();
        this.markOpenRow.set(transaction.uuid);
        let inputs = {
          inputTransaction: transaction
        };
        this.pageEvent.emit(this.paginationOptions())
        this._mainSidenavService.open<boolean>({
          template: ReportsComponent,
          inputs
        }).afterClosed.pipe(take(1)).subscribe({
          next: () => {
            this.markOpenRow.set('');
            this.outputEventUpdateStatuses.emit();
            this.pageEvent.emit(this.paginationOptions());
          }
        });
      }
    });
  }

  public asIngoingTransaction(transaction: AmlScanEntity<LatestAmlReport>): AmlScanEntity<IngoingTransaction> {
    return transaction as AmlScanEntity<IngoingTransaction>;
  }
}
